import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  UsersThree, MagnifyingGlass, MapPin, GameController, CalendarDots,
  Plus, CaretRight, Star, Clock, Lightning
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RecurringGroupsPage() {
  const [groups, setGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('browse');
  const [search, setSearch] = useState({ city: '', game: '' });
  const navigate = useNavigate();

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.city) params.append('city', search.city);
      if (search.game) params.append('game', search.game);
      const [browseRes, myRes] = await Promise.all([
        axios.get(`${API}/api/recurring-groups?${params}`, { withCredentials: true }),
        axios.get(`${API}/api/recurring-groups/my`, { withCredentials: true }),
      ]);
      setGroups(browseRes.data);
      setMyGroups(myRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [search]);

  useEffect(() => { fetch(); }, [fetch]);

  const FREQ_LABEL = { weekly: 'Weekly', biweekly: 'Bi-weekly', monthly: 'Monthly' };
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const GroupCard = ({ g }) => (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4 hover:border-[#9D4CDD]/30 transition-colors cursor-pointer"
      onClick={() => navigate(`/groups/recurring/${g.id}`)}
      data-testid={`group-card-${g.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#9D4CDD]/10 flex items-center justify-center">
            <UsersThree size={18} weight="duotone" className="text-[#9D4CDD]" />
          </div>
          <div>
            <h3 className="text-[14px] font-semibold text-white">{g.name}</h3>
            <div className="flex items-center gap-1.5 text-[11px] text-[#555]">
              <MapPin size={11} /> {g.city}{g.state ? `, ${g.state}` : ''}
            </div>
          </div>
        </div>
        <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#9D4CDD]/20 text-[#9D4CDD] bg-[#9D4CDD]/5 font-medium">
          {g.group_type?.replace(/_/g, ' ')}
        </span>
      </div>
      <p className="text-[12px] text-[#666] mb-3 line-clamp-2">{g.description}</p>
      <div className="flex items-center gap-3 text-[11px] text-[#555]">
        {g.primary_game_id && <span className="flex items-center gap-1"><GameController size={12} /> {g.primary_game_id}</span>}
        {g.recurring_rule && <span className="flex items-center gap-1"><Clock size={12} /> {FREQ_LABEL[g.recurring_rule.frequency] || g.recurring_rule.frequency} — {DAYS[g.recurring_rule.day_of_week]} {g.recurring_rule.time}</span>}
        <span className="flex items-center gap-1"><UsersThree size={12} /> {g.member_count}/{g.max_members}</span>
      </div>
      {g.beginner_friendly && <span className="inline-block mt-2 text-[9px] bg-[#10B981]/10 text-[#10B981] px-2 py-0.5 rounded-full border border-[#10B981]/20">Beginner Friendly</span>}
    </motion.div>
  );

  return (
    <div className="max-w-4xl mx-auto" data-testid="recurring-groups-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Recurring Groups</h1>
          <p className="text-[13px] text-[#444] mt-0.5">Find or create a group that meets regularly</p>
        </div>
        <Button size="sm" className="h-8 text-[11px] bg-[#9D4CDD] hover:bg-[#8B3DC9] text-white"
          onClick={() => navigate('/groups/recurring/create')} data-testid="create-group-btn">
          <Plus size={13} className="mr-1" /> New Group
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-[#0c0c14] rounded-lg p-1 w-fit border border-white/[0.06]">
        {[{ id: 'browse', label: 'Browse' }, { id: 'my', label: 'My Groups' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-md text-[12px] font-medium transition-colors ${tab === t.id ? 'bg-[#9D4CDD]/15 text-[#9D4CDD]' : 'text-[#555] hover:text-[#999]'}`}
            data-testid={`tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {/* Search */}
      {tab === 'browse' && (
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#333]" />
            <input value={search.city} onChange={e => setSearch({ ...search, city: e.target.value })} placeholder="City..."
              className="w-full bg-[#0c0c14] border border-white/[0.06] rounded-md pl-9 pr-3 py-2 text-[13px] text-white focus:border-[#9D4CDD]/30 focus:outline-none placeholder-[#2a2a2a]"
              data-testid="search-city" />
          </div>
          <div className="relative flex-1">
            <GameController size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#333]" />
            <input value={search.game} onChange={e => setSearch({ ...search, game: e.target.value })} placeholder="Game..."
              className="w-full bg-[#0c0c14] border border-white/[0.06] rounded-md pl-9 pr-3 py-2 text-[13px] text-white focus:border-[#9D4CDD]/30 focus:outline-none placeholder-[#2a2a2a]"
              data-testid="search-game" />
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-white/[0.03] rounded-lg animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(tab === 'browse' ? groups : myGroups).map(g => <GroupCard key={g.id} g={g} />)}
          {(tab === 'browse' ? groups : myGroups).length === 0 && (
            <div className="col-span-2 text-center py-12 text-[#444]">
              <UsersThree size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-[13px]">{tab === 'browse' ? 'No groups found' : 'You haven\'t joined any groups yet'}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
